/**
 * The note under the partner's offer: what its status means on this property.
 *
 * One table keyed by offer status and property status, because the same status
 * reads differently once the property has moved on: an accepted offer on a sold
 * property closed, and a rejected one there lost to the outcome rather than to
 * the agent's hand.
 */

import type { PropertyStatus } from '@/features/properties/api/properties'
import type { PartnerOffer } from '../../api/partnerLinks'

export interface OfferNote {
  title?: string
  message: string
}

/** Offer status, with an engaged offer split by who made the newest counter. */
export type OfferNoteKey = PartnerOffer['status'] | 'agent_countered' | 'investor_countered'

const UNDER_CONTRACT_LIVE: OfferNote = {
  title: 'Property under contract',
  message: 'This property went under contract. Your offer stays open until the listing agent closes it.',
}

/**
 * What each status means, and where the property's status changes that.
 * `default` applies to every property status without its own row.
 */
export const OFFER_NOTES: Record<OfferNoteKey, { default: OfferNote } & Partial<Record<PropertyStatus, OfferNote>>> = {
  pending: {
    default: {
      title: 'Awaiting the listing agent',
      message: 'The listing agent has been notified and will review your offer.',
    },
    under_contract: UNDER_CONTRACT_LIVE,
  },
  engaged: {
    default: {
      title: 'The listing agent engaged your offer',
      message: 'The agent is reviewing these terms with you.',
    },
    under_contract: UNDER_CONTRACT_LIVE,
  },
  agent_countered: {
    default: {
      title: 'Agent countered',
      message: 'The terms above are the listing agent’s counter offer.',
    },
  },
  investor_countered: {
    default: {
      title: 'Counter offer sent',
      message: 'Your counter offer went to the listing agent.',
    },
  },
  accepted: {
    default: {
      title: 'Offer accepted',
      message: 'The listing agent will follow up on the contract.',
    },
    under_contract: {
      title: 'Your offer won',
      message: 'The property is under contract on your offer. The listing agent will follow up on the contract.',
    },
    sold: {
      title: 'Sale closed',
      message: 'The sale closed on this offer.',
    },
  },
  rejected: {
    default: { message: 'The listing agent rejected this offer.' },
    sold: { message: 'This property sold to another buyer, so this offer was closed.' },
    removed: { message: 'This property was taken off the market, so this offer was closed.' },
  },
  withdrawn: {
    default: { message: 'You withdrew this offer.' },
  },
}

const PROPERTY_STATUSES: readonly PropertyStatus[] = ['draft', 'available', 'under_contract', 'sold', 'removed']

/** The partner read sends status as a plain string. */
function isPropertyStatus(value: string): value is PropertyStatus {
  return PROPERTY_STATUSES.some((status) => status === value)
}

export function offerNoteKey(offer: Pick<PartnerOffer, 'status' | 'negotiations'>): OfferNoteKey {
  // The first entry is the initial terms recorded on engage; a counter follows it.
  const newest = offer.negotiations.length > 1 ? offer.negotiations[offer.negotiations.length - 1] : undefined
  if (offer.status !== 'engaged' || newest === undefined) return offer.status
  return newest.role === 'agent' ? 'agent_countered' : 'investor_countered'
}

export function offerNote(offer: Pick<PartnerOffer, 'status' | 'negotiations'>, propertyStatus: string): OfferNote {
  const row = OFFER_NOTES[offerNoteKey(offer)]
  const override = isPropertyStatus(propertyStatus) ? row[propertyStatus] : undefined
  return override ?? row.default
}
